import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';

import {
  CoreDocumentTypeItem,
  DocumentFields,
  DocumentsSections,
} from '@cd3p/core/modules/documents';
import { APIDocument, APILanguage } from '@cd3p/core/types/api';
import {
  OmitFirstFourArgs,
  OmitFirstThreeArgs,
  OmitFirstTwoArgs,
} from '@cd3p/core/types/utils';
import { Typography } from '@mui/material';
import {
  Button,
  ConfirmationDialog,
  FormField,
  GlobalLoader,
  MarkDownPreview,
  MenuItem,
  Stack,
} from 'components';
import { DocsSectionsTree } from 'features';
import { useHandleApiResult } from 'hooks/useHandleApiResult';
import { LocationChangeConfirmationContext } from 'providers/LocationChangeConfirmationProvider';
import { TreeProps } from 'react-arborist/src/types/tree-props';

import {
  useBeforeUnload,
  useForm,
  useNavigate,
  useTranslation,
} from 'third-party';

import { helpCenterEditorUrl } from 'constants/url';

import {
  deleteHelpCenterDocument,
  fetchHelpCenterDocument,
  loadHelpCenterDocuments,
  saveHelpCenterDocument,
} from 'modules/helpCenter';
import { NotificationSeverity, useNotifications } from 'modules/notifications';
import {
  deleteReleaseNotesDocument,
  fetchReleaseNotesDocument,
  loadReleaseNotesDocuments,
  saveReleaseNotesDocument,
} from 'modules/releaseNotes';
import {
  deleteTermsAndConditionsDocument,
  fetchTermsAndConditionsDocument,
  loadTermsAndConditionsDocuments,
  saveTermsAndConditionsDocument,
} from 'modules/termsAndConditions';

const NavigationContainer = styled(Stack)`
  min-width: 30rem;
  min-height: 0;
  flex-shrink: 0;
  overflow: scroll;
  padding: 1rem;
  display: flex;
  background-color: white;
  box-shadow: 10px 0 12px -10px rgba(0, 0, 0, 0.25);
`;

const ContentContainer = styled(Stack)`
  flex-grow: 1;
  flex-direction: row;
  min-height: 0;
`;

const HelpContainer = styled(Stack)`
  margin: 2rem 2rem 2rem 3rem;
  flex-grow: 1;
  min-height: 0;
`;

const HelpContainerHeader = styled(Stack)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 1rem;
`;

const ArticleTitle = styled(Typography)`
  margin-bottom: 1rem;
  flex-grow: 1;
  padding-right: 2rem;
`;

const EmptyStateContainer = styled(Stack)`
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

const EmptyStateText = styled(Typography)``;

const TreeWrapper = styled(Stack)`
  flex-grow: 1;
  min-height: 0;
  margin-top: 1rem;
`;

const APPLICATION_ID = 'applicationId';
const LANGUAGE = 'language';

type Props = {
  title: string;
  applicationId: string;
  documentId?: string;
  documentLoading: boolean;
  items: CoreDocumentTypeItem[];
  itemsLoading: boolean;
  itemsLoaded: boolean;
  editorUrl: typeof helpCenterEditorUrl;
  language: APILanguage;
  setLanguage: React.Dispatch<React.SetStateAction<APILanguage>>;
  treeProps?: Partial<TreeProps<CoreDocumentTypeItem>> & {
    hasSections?: boolean;
  };
  editHiddenFields?: DocumentFields[];
  loadDocuments: OmitFirstFourArgs<
    | typeof loadHelpCenterDocuments
    | typeof loadReleaseNotesDocuments
    | typeof loadTermsAndConditionsDocuments
  >;
  deleteDocument: OmitFirstTwoArgs<
    | typeof deleteHelpCenterDocument
    | typeof deleteReleaseNotesDocument
    | typeof deleteTermsAndConditionsDocument
  >;
  fetchDocument: OmitFirstTwoArgs<
    | typeof fetchHelpCenterDocument
    | typeof fetchReleaseNotesDocument
    | typeof fetchTermsAndConditionsDocument
  >;
  saveDocument: OmitFirstThreeArgs<
    | typeof saveHelpCenterDocument
    | typeof saveReleaseNotesDocument
    | typeof saveTermsAndConditionsDocument
  >;
  applications?: { label: string; value: DocumentsSections }[];
};

export const DocsEditor: React.FC<Props> = ({
  title,
  applicationId,
  documentId,
  items,
  itemsLoading,
  itemsLoaded,
  documentLoading,
  loadDocuments,
  saveDocument,
  deleteDocument,
  fetchDocument,
  editorUrl,
  language,
  setLanguage,
  applications,
  treeProps,
  editHiddenFields,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [isDocumentSaving, setIsDocumentSaving] = useState(false);
  const [isLeaveConfirmationDialogShown, setLeaveConfirmationDialogShown] =
    useState(false);
  const [draftText, setDraftText] = useState<string>('');
  const [activeDocument, setActiveDocument] = useState<APIDocument | null>(
    null,
  );

  const { continueChangeLocation, addLocationChangeConfirmation } = useContext(
    LocationChangeConfirmationContext,
  );

  const isDirty = useMemo(() => {
    return !!activeDocument && activeDocument?.text !== draftText;
  }, [activeDocument, draftText]);

  useBeforeUnload(isDirty, t('common.exitConfirmationWindow.text'));

  const onLocationChange = useCallback(() => {
    if (isDirty) {
      setLeaveConfirmationDialogShown(true);
      return false;
    }
  }, [isDirty]);

  useEffect(() => {
    return addLocationChangeConfirmation(onLocationChange);
  }, [onLocationChange, addLocationChangeConfirmation]);

  const handleApiResult = useHandleApiResult();

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (documentId) {
      handleApiResult<APIDocument | null>(
        () => fetchDocument(documentId),
        ({ result }) => {
          if (result.payload) {
            setActiveDocument(result.payload);
            setDraftText(result.payload.text || '');
          } else {
            addNotification({
              severity: NotificationSeverity.Error,
              message: t('common.generalError'),
            });
          }
        },
      );
    }
  }, [fetchDocument, handleApiResult, documentId, addNotification, t]);

  const onDocsTreeDocumentClick = useCallback(
    (document: APIDocument) => {
      !document.isCategory && navigate(editorUrl(applicationId, document.id));
    },
    [navigate, applicationId, editorUrl],
  );

  const applicationsSelectOptions = useMemo(() => {
    return applications?.map(option => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  }, [applications]);

  const languageSelectOptions = useMemo(() => {
    return Object.values(APILanguage).map(language => (
      <MenuItem key={language} value={language}>
        {language}
      </MenuItem>
    ));
  }, []);

  const { control } = useForm({
    defaultValues: {
      [APPLICATION_ID]: applicationId,
      [LANGUAGE]: language,
    },
  });

  const onLanguageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setActiveDocument(null);
      setDraftText('');
      setLanguage(event.target.value as APILanguage);
    },
    [setLanguage],
  );

  const onApplicationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setActiveDocument(null);
      setDraftText('');
      navigate(editorUrl(event.target.value));
    },
    [navigate, editorUrl],
  );

  const onMarkdownChange = useCallback(
    (value: string) => setDraftText(value),
    [],
  );

  const onMarkdownSave = useCallback(() => {
    if (activeDocument) {
      const updatedDocument = { ...activeDocument, text: draftText };
      setIsDocumentSaving(true);
      handleApiResult<APIDocument>(
        () => saveDocument(updatedDocument),
        ({ showBaseToast, result }) => {
          showBaseToast(t('docs.toast.successfullySaved'));
          setIsDocumentSaving(false);
          setActiveDocument(result.payload);
        },
        () => {
          setIsDocumentSaving(false);
        },
      );
    }
  }, [activeDocument, draftText, handleApiResult, saveDocument, t]);

  const onDocsTreeDocumentDelete = useCallback(() => {
    navigate(editorUrl(applicationId));
  }, [navigate, applicationId, editorUrl]);

  const onDocsTreeDocumentSave = useCallback(
    (document: APIDocument) => {
      if (activeDocument && activeDocument.id === document.id) {
        // @ts-ignore
        setActiveDocument(it => ({
          ...it,
          title: document.title || '',
        }));
      }
    },
    [activeDocument],
  );

  return (
    <ContentContainer>
      <NavigationContainer>
        <Typography variant="h4" marginBottom="1rem">
          {title}
        </Typography>
        {applicationsSelectOptions && (
          <FormField
            isRequired={false}
            fieldName={APPLICATION_ID}
            label={t('docs.applicationLabel')}
            control={control}
            selectOptions={applicationsSelectOptions}
            onChange={onApplicationChange}
          />
        )}
        <FormField
          isRequired={false}
          control={control}
          fieldName={LANGUAGE}
          label={t('docs.languageLabel')}
          selectOptions={languageSelectOptions}
          onChange={onLanguageChange}
        />
        <TreeWrapper>
          <DocsSectionsTree
            key={applicationId}
            isReadonly={false}
            isLoading={itemsLoading}
            isLoaded={itemsLoaded}
            selectedDocumentId={documentId}
            sectionId={applicationId as DocumentsSections}
            items={items}
            saveDocument={saveDocument}
            deleteDocument={deleteDocument}
            onDocumentClick={onDocsTreeDocumentClick}
            onAddDocument={onDocsTreeDocumentClick}
            onSaveDocument={onDocsTreeDocumentSave}
            onDeleteDocument={onDocsTreeDocumentDelete}
            treeProps={treeProps}
            hasSections={treeProps?.hasSections}
            editHiddenFields={editHiddenFields}
          />
        </TreeWrapper>
        <ConfirmationDialog
          onClose={() => setLeaveConfirmationDialogShown(false)}
          open={isLeaveConfirmationDialogShown}
          handleActionClick={() => {
            continueChangeLocation();
            setLeaveConfirmationDialogShown(false);
          }}
          description={t('common.exitConfirmationWindow.text')}
          cancelButtonTitle={t('common.continueEditing')}
          actionButtonTitle={t('common.confirmCancel')}
        />
      </NavigationContainer>
      {documentId ? (
        documentLoading || !activeDocument ? (
          <GlobalLoader />
        ) : (
          <HelpContainer>
            <HelpContainerHeader>
              <ArticleTitle variant="h4">{activeDocument?.title}</ArticleTitle>
              <Button
                color="primary"
                variant="contained"
                onClick={onMarkdownSave}
                loading={isDocumentSaving}
                disabled={draftText === activeDocument.text}
              >
                {t('common.save')}
              </Button>
            </HelpContainerHeader>
            <MarkDownPreview
              markdown={draftText ?? ''}
              onMarkdownChange={onMarkdownChange}
            />
          </HelpContainer>
        )
      ) : (
        <EmptyStateContainer>
          <EmptyStateText variant="h4">
            {t('docs.selectDocumentToEdit')}
          </EmptyStateText>
        </EmptyStateContainer>
      )}
    </ContentContainer>
  );
};
